import React, { useEffect, useReducer } from 'react'

import { Dialog, DialogContent, DialogTitle, Button, Box } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import api from '../../../../api'
import { getSession } from '../../../../auth'
import { lockedReducer } from '../../../../utils/defaultReducer'
import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    gridClasses,
    ptBR
} from '@mui/x-data-grid-pro';
import { ptBR as corePtBR } from '@mui/material/locale';
import {
    createTheme,
    ThemeProvider,
    StyledEngineProvider
} from '@mui/material/styles';
import dayjs from 'dayjs'

const qrDisplayIntervalMs = 5 * 60 * 1000

const theme = createTheme({
    palette: {
        primary: { main: '#21446C' },
    },
}, ptBR, corePtBR);

const wrongNumberDisconnectEvent = 'wrong_number_disconnect'

const events_dict = {
    'connect': 'Conectado',
    'disconnect': 'Desconectado',
    [wrongNumberDisconnectEvent]: 'Tentativa com número incorreto',
    'create': 'Criado',
    'remove': 'Excluído',
    'qrcode': 'QR Code gerado',
    'add_ignore': 'Ignorado',
    'remove_ignore': 'Liberado'
}

const eventAliases = {
    connected: 'connect',
    reconnect: 'connect',
    open: 'connect',
    opened: 'connect',
    online: 'connect',
    disconnected: 'disconnect',
    close: 'disconnect',
    closed: 'disconnect',
    offline: 'disconnect',
    created: 'create',
    removed: 'remove',
    qrcode_generated: 'qrcode',
    qr_code: 'qrcode',
    qr_code_generated: 'qrcode',
}

const normalizeEvent = (event) => {
    const normalized = String(event || '')
        .trim()
        .toLowerCase()
        .replace(/[\s.-]+/g, '_')
        .replace(/^_+|_+$/g, '')
    return eventAliases[normalized] || normalized
}

const isWrongNumberDetails = (details) => {
    const parsedDetails = parseDetails(details)
    return parsedDetails.wrongNumber === true || parsedDetails.wrongNumber === 'true'
}

const compactHistoryRows = (rows = []) => {
    const compactedRows = []
    let pendingQrRow = null
    let pendingQrTimestamp = null
    const displayedWrongNumberDisconnectDates = new Set()
    const getRowDate = (row) => dayjs.utc(row.created_at).local().format('DD/MM/YYYY HH:mm')
    const getRowTimestamp = (row) => dayjs.utc(row.created_at).local().valueOf()
    const wrongNumberDisconnectDates = new Set(
        rows
            .filter((row) => (
                normalizeEvent(row.event) === 'disconnect' &&
                isWrongNumberDetails(row.details)
            ))
            .map(getRowDate)
    )

    const pushPendingQr = () => {
        if (pendingQrRow) {
            compactedRows.push(pendingQrRow)
            pendingQrRow = null
            pendingQrTimestamp = null
        }
    }

    rows.forEach((row) => {
        const normalized = normalizeEvent(row.event)
        const rowDate = getRowDate(row)
        const rowTimestamp = getRowTimestamp(row)
        const isWrongNumberDisconnect = normalized === 'disconnect' && isWrongNumberDetails(row.details)

        if (
            normalized === 'disconnect' &&
            !isWrongNumberDisconnect &&
            wrongNumberDisconnectDates.has(rowDate)
        ) {
            return
        }

        if (isWrongNumberDisconnect && displayedWrongNumberDisconnectDates.has(rowDate)) {
            return
        }

        if (normalized === 'qrcode') {
            if (
                pendingQrRow &&
                Number.isFinite(rowTimestamp) &&
                Number.isFinite(pendingQrTimestamp) &&
                Math.abs(pendingQrTimestamp - rowTimestamp) < qrDisplayIntervalMs
            ) {
                pendingQrRow = row
                pendingQrTimestamp = rowTimestamp
                return
            }

            pushPendingQr()
            pendingQrRow = row
            pendingQrTimestamp = rowTimestamp
            return
        }

        pushPendingQr()

        if (isWrongNumberDisconnect) {
            displayedWrongNumberDisconnectDates.add(rowDate)
        }

        compactedRows.push(row)
    })

    pushPendingQr()

    return compactedRows
}

const parseDetails = (details) => {
    if (!details) return {}
    if (typeof details === 'object') return details

    try {
        return JSON.parse(details)
    } catch {
        return {}
    }
}

const renderEvent = (event, details) => {
    const parsedDetails = parseDetails(details)
    const normalized = normalizeEvent(event)

    if (
        normalized === 'disconnect' &&
        isWrongNumberDetails(details)
    ) {
        return events_dict[wrongNumberDisconnectEvent]
    }

    if (parsedDetails.wasReconnect === true) return events_dict.connect
    if (parsedDetails.autoReconnect === true) return events_dict.disconnect

    return events_dict[normalized] || normalized || 'Evento não identificado'
}

const defColumns = (timezone) => {
    const columns = []
    columns.push({ field: 'phone_number', headerName: 'Telefone', flex: 1 })
    columns.push({ field: 'name', headerName: 'Nome', flex: 1 })
    columns.push({
        field: 'event', headerName: 'Evento', flex: 1
        , renderCell: ({ value, row }) => renderEvent(value, row?.details)
    })
    columns.push({
        field: 'details', headerName: 'Alvo', flex: 1
        , renderCell: ({ value }) => (value?.split('@')[0] || '')
    })
    columns.push({
        field: 'created_at', headerName: 'Data', flex: 1
        , renderCell: ({ value }) => dayjs.utc(value).local().format('DD/MM/YYYY HH:mm')
    })

    return columns
}

const getData = async (data, setState, enterprise) => {
    const res = await api.get(`api/enterprises/${enterprise.id}/scout_accounts/${data.id}/history/`)
    if (res.status === 200)
        setState({rows: compactHistoryRows(res.data)})
}

const History = ({ data, handleClose }) => {

    const enterprise = getSession().profile.enterprise
    const timezone = enterprise.settings.timezone

    const [state, setState] = useReducer(lockedReducer, {
        rows: []
    })

    useEffect(() => {
        getData(data, setState, enterprise)
    }, [])

    return (
        <Dialog
            open={true}
            fullWidth
            disablePortal
            disableEnforceFocus
            maxWidth={'md'}
            sx={{ position: "absolute" }}
        >
            <DialogTitle className="group-flexbox">
                Histórico de conexões
                <Button sx={{ minWidth: "2rem", padding: "0" }} onClick={handleClose}>
                    <CloseIcon />
                </Button>
            </DialogTitle>
            <DialogContent>
                <Box className="modal-scout-table">
                    <StyledEngineProvider injectFirst >
                        <ThemeProvider theme={theme}>
                            <DataGridPro
                                pageSize={100}
                                pagination
                                disableSelectionOnClick
                                disableDensitySelector
                                density="compact"
                                columns={defColumns(timezone)}
                                rows={state.rows}
                            />
                        </ThemeProvider>
                    </StyledEngineProvider>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default History
